import styled from 'styled-components'

import { fromMd, untilMd } from '@syconium/little-miss-figgy'

import { NextLink } from '../../../../../../../../app/_components/navigation/NextLink'
import { EditorialCellTextAlignment } from '../../../../../../../../types/graphql/collection-view'
import {
  EditorialCellDescriptionPosition,
  EditorialCellDescriptionSize,
  EditorialCellTitle,
} from '../types'

export const Container = styled.div<{
  isInverted: boolean | null
  isFloating: boolean | null
}>`
  display: flex;
  margin-bottom: 32px;

  ${fromMd} {
    flex-direction: ${o => (o.isInverted ? 'column-reverse' : 'column')};
  }

  ${untilMd} {
    flex-direction: column;
  }
`

export const Tile = styled.div<{
  backgroundColor: string | null
  hasBottomPadding: boolean
}>`
  background-color: ${o => o.backgroundColor};
  overflow: hidden;
  text-transform: uppercase;
  position: relative;
  line-height: 0px;
  ${fromMd} {
    margin-bottom: ${o => (o.hasBottomPadding ? '60px' : 0)};
  }
`

export const VideoWrapper = styled.div`
  height: calc(9 / 16 * 54vw);

  ${untilMd} {
    height: 112vw;
  }
`

export const Video = styled.video`
  left: 0;
  object-fit: cover;
  font-family: 'object-fit: cover;';
  top: 0;
  width: 100%;

  ${fromMd} {
    position: absolute;
  }
`

const titleFontSizeDesktop = (size?: EditorialCellTitle | null) => {
  const sizes = {
    [EditorialCellTitle['Large (Text Only)']]: '56px',
    [EditorialCellTitle['Medium (Two Up or Wide)']]: '36px',
  }

  return (size && sizes[size]) || '28px'
}

const titleFontSizeMobile = (size?: EditorialCellTitle | null) => {
  const sizes = {
    [EditorialCellTitle['Large (Text Only)']]: '25px',
    [EditorialCellTitle['Medium (Two Up or Wide)']]: '20px',
  }

  return (size && sizes[size]) || '18px'
}

export const Title = styled.h2<{
  titleAlignment?: string
  textColor?: string
  titleSize?: EditorialCellTitle | null
}>`
  font-size: ${o => titleFontSizeDesktop(o.titleSize)};
  font-weight: 700;
  letter-spacing: 5.4px;
  line-height: 48px;
  text-align: ${o => o.titleAlignment || 'left'};
  text-transform: uppercase;
  ${fromMd} {
    color: ${o => o.textColor || '#000'};
  }
  ${untilMd} {
    font-size: ${o => titleFontSizeMobile(o.titleSize)};
    letter-spacing: 3px;
    line-height: 28px;
  }
`

export const Subtitle = styled.p<{
  titleAlignment?: string
  textColor?: string
}>`
  font-weight: 600;
  line-height: 22px;
  font-size: 13px;
  letter-spacing: 0.65px;
  margin-top: 12px;
  text-align: ${o => o.titleAlignment || 'left'};
  ${fromMd} {
    color: ${o => o.textColor || '#000'};
  }

  ${untilMd} {
    font-size: 13px;
  }
`

export const DescriptionBody = styled.div<{
  paddingX?: number | null
  isFloating?: boolean
}>`
  ${fromMd} {
    margin-top: 16px;
    padding: 0 ${o => (o.paddingX && `${o.paddingX}px`) || (o.isFloating && '6%')};
  }
`

export const DescriptionWrapper = styled.div<{
  textAlign?: EditorialCellTextAlignment | null
}>`
  text-align: ${o => o.textAlign};
  margin-top: 16px;
  width: 100%;

  ${untilMd} {
    margin-bottom: 16px;
  }
`

const descriptionFontSizeDesktop = (size: EditorialCellDescriptionSize | null | undefined) => {
  const sizes = {
    [EditorialCellDescriptionSize['Large (Text Only or Two Up)']]: '28px',
    [EditorialCellDescriptionSize['Medium (Text Only)']]: '18px',
    [EditorialCellDescriptionSize['Medium (Single Tile)']]: '16px',
    [EditorialCellDescriptionSize['Small (Text Only, Two Up, or Wide)']]: '13px',
  }

  return (size && sizes[size]) || '13px'
}

const descriptionFontSizeMobile = (size: EditorialCellDescriptionSize | null | undefined) => {
  const sizes = {
    [EditorialCellDescriptionSize['Large (Text Only or Two Up)']]: '17px',
    [EditorialCellDescriptionSize['Medium (Text Only)']]: '16px',
    [EditorialCellDescriptionSize['Medium (Single Tile)']]: '16px',
    [EditorialCellDescriptionSize['Small (Text Only, Two Up, or Wide)']]: '12px',
  }

  return (size && sizes[size]) || '16px'
}

const descriptionLineHeightDesktop = (size: EditorialCellDescriptionSize | null | undefined) =>
  size === EditorialCellDescriptionSize['Large (Text Only or Two Up)'] ? '42px' : '22px'

const descriptionLineHeightMobile = (size: EditorialCellDescriptionSize | null | undefined) =>
  size === EditorialCellDescriptionSize['Large (Text Only or Two Up)'] ? '26px' : '20px'

export const Description = styled.p<{
  fontSize: EditorialCellDescriptionSize | null | undefined
  descriptionColor?: string | null
}>`
  font-size: ${o => descriptionFontSizeDesktop(o.fontSize)};
  font-weight: 700;
  letter-spacing: 1.5px;
  margin-top: 12px;
  text-transform: initial;
  line-height: ${o => descriptionLineHeightDesktop(o.fontSize)};

  ${fromMd} {
    color: ${o => o.descriptionColor};
  }
  ${untilMd} {
    font-size: ${o => descriptionFontSizeMobile(o.fontSize)};
    line-height: ${o => descriptionLineHeightMobile(o.fontSize)};
    letter-spacing: 0.6px;
  }
`
export const LinkDescription = styled.p<{
  textAlign?: EditorialCellTextAlignment | null
}>`
  padding-top: 10px;
  font-weight: 700;
  line-height: 21px;
  letter-spacing: 2.0999999046325684px;
  text-align: ${o => o.textAlign};
  font-size: 14px;
  text-transform: uppercase;
  ${untilMd} {
    font-size: 13px;
  }
`

export const StarWrapper = styled.div<{
  textAlign?: EditorialCellTextAlignment | null
}>`
  display: flex;
  margin-top: 12px;
  margin-bottom: 12px;
  justify-content: ${o => o.textAlign};
`

export const Cta = styled.span<{ color?: string }>`
  border-bottom: 2px solid currentColor;
  cursor: pointer;
  display: inline-block;
  font-weight: 700;
  line-height: 20px;
  font-size: 13px;
  letter-spacing: 2px;
  margin-top: 16px;
  text-transform: uppercase;
  ${fromMd} {
    color: ${o => o.color || '#000'};
  }
  ${untilMd} {
    font-size: 13px;
  }
`

export const MediaWrapper = styled.div<{ cellWidth?: number | null }>`
  flex: 1;
  overflow: hidden;
  min-height: ${o => o.cellWidth === 4 && '500px'};

  ${untilMd} {
    min-height: ${o => (o.cellWidth === 4 ? '130px' : '292px')};
  }
`

export const TextWrapper = styled.div<{
  placement?: keyof typeof EditorialCellDescriptionPosition | null
  isTextOnly?: boolean | null
  isFloating?: boolean | null
  textAlignment?: string | null
}>`
  padding: ${o => (o.placement && o.isTextOnly ? '40px' : '0px')};
  ${fromMd} {
    position: ${o => (o.isFloating ? 'absolute' : 'initial')};

    height: ${o => (o.isFloating ? 'calc(100% - 8%)' : 'initial')};
    margin-bottom: 32px;
  }
  ${untilMd} {
    margin-top: 16px;
    margin-bottom: 16px;

    padding-left: ${o => (o.placement && o.isTextOnly ? '56px' : '16px')};

    padding-right: ${o => (o.placement && o.isTextOnly ? '56px' : '16px')};
  }

  top: ${o => (o.isFloating ? 0 : 'auto')};
  box-sizing: border-box;

  width: ${o => (o.isFloating ? '100%' : 'initial')};

  display: ${o => (o.isFloating ? 'flex' : 'initial')};

  align-items: ${o => (o.isFloating ? 'flex-end' : 'initial')};
  text-align: ${o => o.textAlignment};
`

export const Eyebrow = styled.div<{
  textAlign?: EditorialCellTextAlignment | null
}>`
  margin-top: 12px;
  margin-bottom: 12px;
  font-weight: 700;
  font-size: 11px;
  line-height: 16px;
  text-align: center;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  font-style: normal;
  text-align: ${o => o.textAlign || 'left'};
`

export const Disclaimer = styled.div`
  position: absolute;
  bottom: ${o => o.theme.spacing(2)};
  margin-top: 0;
  text-align: center;
  padding: 0 ${o => o.theme.spacing(1)};
  box-sizing: border-box;
  width: 100%;
  /* TODO: This should technically change based on the image used... */
  color: ${o => o.theme.color.text.highContrast.on.darkImage};
  display: flex;
  justify-content: center;
  /* Sit above StretchedLink, but only let the disclaimer's own links take clicks */
  z-index: 2;
  pointer-events: none;

  a {
    pointer-events: auto;
  }
`

// Covers the Tile so the media isn't nested inside the <a> (iframes can't be <a> descendants)
export const StretchedLink = styled(NextLink)`
  position: absolute;
  inset: 0;
  z-index: 1;

  /* Tile clips overflow, so draw NextLink's focus outline inside the edge */
  &:before {
    outline-offset: -${o => o.theme.spacing(1)};
  }
`
